import { useState, useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || "";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "ok"; issueNumber?: number }
  | { kind: "error"; message: string };

export default function ContactForm({ open, onClose }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [token, setToken] = useState<string>("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !TURNSTILE_SITE_KEY) return;
    const ensureScript = () =>
      new Promise<void>((resolve) => {
        if (window.turnstile) return resolve();
        const existing = document.querySelector(
          'script[src*="challenges.cloudflare.com/turnstile"]'
        );
        if (existing) {
          existing.addEventListener("load", () => resolve());
          return;
        }
        const s = document.createElement("script");
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.head.appendChild(s);
      });

    let cancelled = false;
    ensureScript().then(() => {
      if (cancelled || !turnstileRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (t: string) => setToken(t),
        "error-callback": () => setToken(""),
        "expired-callback": () => setToken(""),
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [open]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ kind: "submitting" });
    try {
      const res = await fetch("/api/contact/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, turnstileToken: token }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setStatus({
          kind: "error",
          message: data.message || data.error || "Submission failed",
        });
        return;
      }
      setStatus({ kind: "ok", issueNumber: data.issueNumber });
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Network error",
      });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          border: "1px solid rgba(217, 119, 87, 0.3)",
          width: "min(460px, 100%)",
          padding: "2rem",
          fontFamily: "var(--mono)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1.125rem", color: "var(--accent)" }}>
            // contact
          </h2>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--fg-dim)",
              fontSize: "1.25rem",
              cursor: "pointer",
              padding: "0.25rem 0.5rem",
            }}
            aria-label="close"
          >
            ×
          </button>
        </div>

        {status.kind === "ok" ? (
          <div style={{ color: "var(--term-green)", fontSize: "0.9375rem", lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              {">"} message received{status.issueNumber ? ` (#${status.issueNumber})` : ""}.
            </p>
            <p>i'll get back to you. probably from a beach. or pre-dawn.</p>
            <button
              onClick={onClose}
              style={{ marginTop: "0.5rem" }}
            >
              close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Field label="name" required value={name} onChange={setName} />
            <Field
              label="email"
              required
              type="email"
              value={email}
              onChange={setEmail}
            />
            <Field
              label="message"
              required
              multiline
              value={message}
              onChange={setMessage}
            />
            {TURNSTILE_SITE_KEY ? (
              <div ref={turnstileRef} />
            ) : (
              <p style={{ fontSize: "0.75rem", color: "var(--fg-dim)", margin: 0 }}>
                (turnstile not configured — dev mode)
              </p>
            )}
            {status.kind === "error" && (
              <p style={{ color: "#ef4444", fontSize: "0.8125rem", margin: 0 }}>
                {status.message}
              </p>
            )}
            <button
              type="submit"
              disabled={
                status.kind === "submitting" ||
                !name ||
                !email ||
                !message ||
                (Boolean(TURNSTILE_SITE_KEY) && !token)
              }
              style={{
                background: "var(--accent)",
                color: "var(--bg)",
                border: "1px solid var(--accent)",
                padding: "0.75rem 1rem",
                cursor: status.kind === "submitting" ? "wait" : "pointer",
                opacity: status.kind === "submitting" ? 0.6 : 1,
                fontWeight: 600,
              }}
            >
              {status.kind === "submitting" ? "sending..." : "send →"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  multiline?: boolean;
}) {
  const baseStyle: React.CSSProperties = {
    width: "100%",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "var(--fg)",
    padding: "0.625rem 0.75rem",
    fontFamily: "var(--mono)",
    fontSize: "0.9375rem",
    outline: "none",
    boxSizing: "border-box",
  };
  return (
    <label style={{ display: "block" }}>
      <span
        style={{
          display: "block",
          fontSize: "0.75rem",
          color: "var(--fg-dim)",
          marginBottom: "0.35rem",
          letterSpacing: "0.05em",
        }}
      >
        {label}
        {required && <span style={{ color: "var(--accent)" }}> *</span>}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          rows={5}
          style={{ ...baseStyle, resize: "vertical" }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          style={baseStyle}
        />
      )}
    </label>
  );
}
